import { useEffect, useState } from "react";
import { Search, Printer, Medal } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import PageHero from "@/components/PageHero";

export default function Results() {
  const [tab, setTab] = useState("results");
  const [events, setEvents] = useState([]);
  const [eventSlug, setEventSlug] = useState("");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cert, setCert] = useState(null);
  const [myForm, setMyForm] = useState({ email: "", reg_id: "" });
  const [myRegs, setMyRegs] = useState(null);

  useEffect(() => {
    api.get("/events").then((r) => {
      const withResults = r.data.filter((e) => e.results?.length > 0);
      setEvents(withResults);
      if (withResults[0]) setEventSlug(withResults[0].slug);
    }).catch(() => {});
  }, []);

  const search = async () => {
    if (!eventSlug) return;
    setBusy(true);
    setCert(null);
    try {
      const { data } = await api.get(`/results/search?event=${eventSlug}&q=${encodeURIComponent(q)}`);
      setData(data);
      if (tab === "certificates") {
        if (data.results.length === 0) toast.error("No participant found. Check the name / bib number.");
        else setCert({ ...data.results[0], eventName: data.event, date: data.date });
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const lookupMy = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.get(`/registrations/lookup?email=${encodeURIComponent(myForm.email)}&reg_id=${encodeURIComponent(myForm.reg_id)}`);
      setMyRegs(data);
      if (data.length === 0) toast.error("No registration found. Check your email or registration ID.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="results-page">
      <PageHero eyebrow="RESULTS & CERTIFICATES" title="Find Your Finish Time."
        subtitle="Search official SPORTIVE event results and download your finisher certificate."
        image="https://images.unsplash.com/photo-1766970096331-78c8af007a3b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwzfHxtYXJhdGhvbiUyMHJ1bm5lcnMlMjBmaW5pc2glMjBsaW5lJTIwdmljdG9yeSUyMGF0aGxldGV8ZW58MHx8fHwxNzg5MDY4MTIzfDA&ixlib=rb-4.1.0&q=85" />

      <section className="mx-auto max-w-4xl px-4 py-16 sm:px-6">
        <div className="flex gap-2">
          {[["results", "RESULTS"], ["certificates", "CERTIFICATES"], ["my", "MY REGISTRATION"]].map(([k, label]) => (
            <button key={k} data-testid={`tab-${k}`} onClick={() => { setTab(k); setCert(null); }}
              className={`rounded-full px-6 py-2.5 font-mono text-xs tracking-[0.2em] transition-colors ${tab === k ? "bg-gradient-to-r from-brand-cyan to-brand-olive text-brand-ink font-bold" : "border border-brand-line text-slate-400 hover:text-white"}`}>
              {label}
            </button>
          ))}
        </div>

        <div className="card-k mt-8 space-y-4 p-7">
          <p className="font-mono text-xs tracking-[0.2em] text-slate-500">{tab === "results" ? "SEARCH BY EVENT, BIB NUMBER, NAME OR CATEGORY" : "DOWNLOAD YOUR ACHIEVEMENT — ENTER NAME OR BIB NUMBER"}</p>
          <div className="grid gap-4 sm:grid-cols-2">
            <select data-testid="results-event-select" className="input-k" value={eventSlug} onChange={(e) => setEventSlug(e.target.value)}>
              {events.length === 0 && <option value="">No published results yet</option>}
              {events.map((e) => <option key={e.slug} value={e.slug}>{e.name}</option>)}
            </select>
            <input data-testid="results-query" className="input-k" placeholder={tab === "results" ? "Name / Bib / Category (optional)" : "Your name or bib number"} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} />
          </div>
          <button data-testid="results-search-btn" onClick={search} disabled={busy || !eventSlug} className="btn-kinetic btn-primary-k justify-center disabled:opacity-60">
            <Search size={16} /> {busy ? "Searching..." : tab === "results" ? "FIND RESULTS" : "GET CERTIFICATE"}
          </button>
        </div>

        {tab === "results" && data && (
          <div data-testid="results-table" className="card-k mt-8 overflow-hidden">
            <div className="border-b border-brand-line px-6 py-4">
              <p className="font-display text-xl font-bold text-white">{data.event}</p>
              <p className="font-mono text-[11px] text-slate-500">{data.date} — {data.results.length} finisher(s)</p>
            </div>
            {data.results.length === 0 ? <p className="px-6 py-8 text-sm text-slate-500">No matching results found.</p> : (
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-brand-line font-mono text-[10px] tracking-[0.2em] text-slate-500">
                    <th className="px-6 py-3">POS</th><th className="px-6 py-3">BIB</th><th className="px-6 py-3">NAME</th><th className="px-6 py-3">CATEGORY</th><th className="px-6 py-3">TIME</th>
                  </tr>
                </thead>
                <tbody>
                  {data.results.map((r, i) => (
                    <tr key={i} className="border-b border-brand-line/50 last:border-0 hover:bg-white/[0.03]">
                      <td className="px-6 py-3.5 font-mono font-semibold text-brand-olive">{r.position}</td>
                      <td className="px-6 py-3.5 font-mono text-brand-cyan">{r.bib}</td>
                      <td className="px-6 py-3.5 font-medium text-white">{r.name}</td>
                      <td className="px-6 py-3.5 text-slate-400">{r.category}</td>
                      <td className="px-6 py-3.5 font-mono text-white">{r.time}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {tab === "certificates" && cert && (
          <div className="mt-8">
            <div id="certificate-print" data-testid="certificate-card" className="relative overflow-hidden rounded-2xl border-2 border-brand-olive/60 bg-brand-surface p-10 text-center">
              <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-brand-cyan to-brand-olive" />
              <img src="/sportive-logo.png" alt="SPORTIVE" className="mx-auto h-14" />
              <p className="mt-6 font-mono text-[11px] tracking-[0.35em] text-brand-olive">CERTIFICATE OF ACHIEVEMENT</p>
              <p className="mt-6 text-sm text-slate-400">This is to certify that</p>
              <p className="mt-2 font-display text-4xl font-extrabold uppercase text-white">{cert.name}</p>
              <p className="mt-4 text-sm leading-relaxed text-slate-300">successfully completed <span className="font-semibold text-brand-cyan">{cert.category}</span> at the<br /><span className="font-display text-xl font-bold text-white">{cert.eventName}</span><br />{cert.date}</p>
              <div className="mx-auto mt-6 flex max-w-xs items-center justify-center gap-8 font-mono">
                <div><p className="text-[10px] tracking-[0.2em] text-slate-500">BIB</p><p className="text-lg font-semibold text-brand-cyan">{cert.bib}</p></div>
                <div><p className="text-[10px] tracking-[0.2em] text-slate-500">TIME</p><p className="text-lg font-semibold text-white">{cert.time}</p></div>
                <div><p className="text-[10px] tracking-[0.2em] text-slate-500">POSITION</p><p className="text-lg font-semibold text-brand-olive">{cert.position}</p></div>
              </div>
              <p className="mt-8 flex items-center justify-center gap-2 font-mono text-[10px] tracking-[0.3em] text-slate-500"><Medal size={13} className="text-brand-olive" /> RUN • EXPLORE • CONNECT</p>
            </div>
            <button data-testid="certificate-download" onClick={() => window.print()} className="btn-kinetic btn-primary-k mt-6 w-full justify-center"><Printer size={16} /> DOWNLOAD / PRINT CERTIFICATE</button>
          </div>
        )}
        {tab === "my" && (
          <div className="card-k mt-8 p-7">
            <p className="font-mono text-xs tracking-[0.2em] text-slate-500">FIND YOUR REGISTRATION — ENTER YOUR EMAIL OR REGISTRATION ID</p>
            <form onSubmit={lookupMy} className="mt-4 grid gap-4 sm:grid-cols-2">
              <input data-testid="myreg-email" type="email" placeholder="Email used at registration" className="input-k" value={myForm.email} onChange={(e) => setMyForm({ ...myForm, email: e.target.value })} />
              <input data-testid="myreg-id" placeholder="Registration ID (e.g. SPR-ABC123)" className="input-k" value={myForm.reg_id} onChange={(e) => setMyForm({ ...myForm, reg_id: e.target.value })} />
              <button data-testid="myreg-search-btn" disabled={busy} className="btn-kinetic btn-primary-k justify-center disabled:opacity-60 sm:col-span-2"><Search size={16} /> {busy ? "Searching..." : "FIND MY REGISTRATION"}</button>
            </form>
            {myRegs && myRegs.length > 0 && (
              <div data-testid="myreg-results" className="mt-6 space-y-3">
                {myRegs.map((r) => (
                  <div key={r.id} className="flex flex-wrap items-center gap-x-6 gap-y-1 rounded-xl border border-brand-line bg-brand-ink/60 px-5 py-4">
                    <span className="font-mono text-sm font-semibold text-brand-olive">{r.reg_id}</span>
                    <span className="font-medium text-white">{r.event_name}</span>
                    <span className="font-mono text-xs text-brand-cyan">{r.distance}</span>
                    <span className="text-xs text-slate-400">{r.name} • {r.email}</span>
                    <span className="ml-auto rounded-full border border-emerald-400/40 bg-emerald-400/10 px-3 py-0.5 font-mono text-[10px] tracking-widest text-emerald-400">{r.status?.toUpperCase() || "RECEIVED"}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
